<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import strtotime from 'locutus/php/datetime/strtotime'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import WithdrawInfo from '@components/admin/withdraw-info'
import {
  fetchWithdrawList,
  rejectWithdraw,
  approveWithdraw,
} from '@apis/admin/withdraw'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import find from 'lodash/find'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'
import { fetchAssets } from '@apis/admin/asset'
import forEach from 'lodash/forEach'

export default {
  components: { PageNavigation, WithdrawInfo },
  data() {
    return {
      loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      s_withdraw_seq: 0,
      show_dialog: false,
      excel_headers: [
        ['seq', this.$t('withdraw_list.seq')],
        ['account_seq', this.$t('withdraw_list.account_seq')],
        ['member_seq', this.$t('withdraw_list.member_seq')],
        [
          'withdraw_address_certification_seq',
          this.$t('withdraw_list.withdraw_address_certification_seq'),
        ],
        ['user_id', this.$t('withdraw_list.user_id')],
        ['user_name', this.$t('withdraw_list.user_name')],
        ['email_address', this.$t('withdraw_list.email_address')],
        ['account_number', this.$t('withdraw_list.account_number')],
        ['holder_name', this.$t('withdraw_list.holder_name')],
        ['bank_code', this.$t('withdraw_list.bank_code')],
        ['fee_type', this.$t('withdraw_list.fee_type')],
        ['fee_amount', this.$t('withdraw_list.fee_amount')],
        ['net_amount', this.$t('withdraw_list.net_amount')],
        ['asset_title', this.$t('withdraw_list.asset_title')],
        ['asset_code', this.$t('withdraw_list.asset_code')],
        ['request_amount', this.$t('withdraw_list.request_amount')],
        ['status', this.$t('withdraw_list.status')],
        ['regdate', this.$t('withdraw_list.regdate')],
        ['withdraw_address', [this.$t('withdraw_list.withdraw_address')]],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('withdraw_list.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('withdraw_list.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
      select_all: false,
      select_list: [],
      show_select_dialog: false,
      s_handle_type: null,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
    c_s_list() {
      if (!this.select_list.length) return []

      return map(this.select_list, (info) => {
        return find(this.list, { seq: info })
      })
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchWithdrawList({
        list_count: 100,
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadAssets() {
      const { data } = await fetchAssets()
      this.assets = data
    },
    async handleReject(seq) {
      try {
        this.loading = true

        if (
          !(await this.$confirm(
            this.$t('admin.msg_denied'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await rejectWithdraw(seq)
        await this.loadList()
        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_denied_success'),
          type: 'success',
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleApprove(seq) {
      try {
        this.loading = true

        if (
          !(await this.$confirm(
            this.$t('admin.msg_approve'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await approveWithdraw(seq)
        await this.loadList()
        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_approve_success'),
          type: 'success',
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('withdraw_list.msg_server_overload'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchWithdrawList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('withdraw_list.withdrawal_application_details', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'bank_code':
                return fromPairs(
                  this.constants.kyc.asset_withdraw_address_certification
                    .bank_codes
                )[val]
              case 'status':
                return fromPairs(this.constants.withdraw[key])[val]
              case 'fee_type':
                return fromPairs(this.constants.withdraw.fee_type)[val]
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    handleView(seq) {
      this.s_withdraw_seq = seq
      this.show_dialog = true
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'asset_seq')
      this.search()
    },
    checkAll() {
      if (this.select_list.length < this.list.length) {
        this.select_list = map(this.list, (info) => {
          return info.seq
        })
      } else this.select_list = []
    },
    handleSelectView(type) {
      this.show_select_dialog = true
      this.s_handle_type = type
    },
    async handleSelectSubmit() {
      const approveOrReject =
        this.s_handle_type === 'approve' ? approveWithdraw : rejectWithdraw
      const confirm_message =
        this.s_handle_type === 'approve'
          ? this.$t('withdraw_list.msg_approve')
          : this.$t('withdraw_list.msg_reject')
      const success_message =
        this.s_handle_type === 'approve'
          ? this.$t('withdraw_list.msg_approved')
          : this.$t('withdraw_list.msg_rejected')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(
          forEach(this.c_s_list, (info) => {
            if (info.status !== 'PENDING') return
            approveOrReject(info.seq)
          })
        )
        .then(() => {
          this.loadList()
          this.show_select_dialog = false
          this.select_list = []
          this.select_all = false
        })
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    strtotime,
    fromPairs,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">
      {{ $t('admin.search') }}
    </h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t('withdraw_list.statistics_exclude_members')
                }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{
                  $t('withdraw_list.withdrawal_members_excluded')
                }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.status') }}</div></th
            >
            <td>
              <ElRadioGroup v-model="search_params.status" size="mini">
                <ElRadioButton
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.withdraw.status,
                  ]"
                  :key="val[0]"
                  :label="val[0]"
                  >{{ val[1] }}</ElRadioButton
                >
              </ElRadioGroup>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['last_login', $t('admin.last_login')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="
                  search_params = {}
                  search
                "
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin_main.withdraw_list') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <ul :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...assets,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.asset_seq }"
        :to="{ query: { ...$route.query, asset_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>{{ title }}</a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col">
            <input v-model="select_all" type="checkbox" @click="checkAll" />
          </th>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('wallet.account_number') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.asset_title') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.modal_asset_code') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.request_amount') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('withdraw_list.charging_type') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('withdraw_list.charge_fee') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('withdraw_list.final_amount') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.status') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
          <th scope="col"
            ><div
              ><a
                href="#"
                :class="[$style.button, $style.blue]"
                @click.prevent="handleSelectView('approve')"
                ><span>{{ $t('withdraw_list.full_approval') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleSelectView('reject')"
                ><span>{{ $t('withdraw_list.total_deny') }}</span></a
              ></div
            ></th
          >
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(data, idx) in list"
          :key="data.seq"
          :class="$style[`bg${idx % 2}`]"
        >
          <td :class="[$style.tCenter, $style.nowrap]"
            ><input v-model="select_list" :value="data.seq" type="checkbox"
          /></td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
          <template v-if="!$route.params.member_seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{ data.user_id }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.user_name
            }}</td>
            <td :class="[$style.nowrap]">{{ data.email_address }}</td>
          </template>
          <td :class="[]">{{ data.account_number }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.asset_title
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.asset_code
          }}</td>
          <td :class="[$style.tRight, $style.nowrap]">{{
            data.request_amount
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            fromPairs(constants.withdraw.fee_type)[data.fee_type]
          }}</td>
          <td :class="[$style.tRight, $style.nowrap]">{{ data.fee_amount }}</td>
          <td :class="[$style.tRight, $style.nowrap]">{{ data.net_amount }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            fromPairs(constants.withdraw.status)[data.status]
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            date('Y-m-d H:i:s', new Date(data.regdate))
          }}</td>
          <td :class="$style.tCenter">
            <a
              href="#"
              :class="[$style.button]"
              @click.prevent="handleView(data.seq)"
              ><span>{{ $t('kyc.detail') }}</span></a
            >
            <template v-if="data.status === 'PENDING'">
              <a
                href="#"
                :class="[$style.button, $style.blue]"
                @click.prevent="handleApprove(data.seq)"
                ><span>{{ $t('admin.approve') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleReject(data.seq)"
                ><span>{{ $t('admin.denied') }}</span></a
              >
            </template>
          </td>
        </tr>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 8 : 11"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 8 : 11"
            :class="$style.tCenter"
            >{{ $t('admin.no_search_withdraw') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <ElDialog :visible.sync="show_dialog" :title="$t('admin.withdraw_detail')">
      <WithdrawInfo
        v-if="s_withdraw_seq"
        :key="s_withdraw_seq"
        :withdraw_seq="s_withdraw_seq"
      ></WithdrawInfo>
      <el-table v-else :data="c_s_list"></el-table>
    </ElDialog>
    <ElDialog
      :visible.sync="show_select_dialog"
      :title="$t('withdraw_list.selected_withdrawal')"
    >
      <el-table :data="c_s_list">
        <el-table-column
          property="user_id"
          :label="$t('admin.user_id')"
        ></el-table-column>
        <el-table-column
          property="account_number"
          :label="$t('wallet.account_number')"
        ></el-table-column>
        <el-table-column
          property="asset_title"
          :label="$t('admin.asset_title')"
        ></el-table-column>
        <el-table-column
          property="net_amount"
          :label="$t('withdraw_list.net_amount')"
        ></el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="show_select_dialog = false">{{
          $t('withdraw_list.cancellation')
        }}</el-button>
        <el-button type="primary" @click="handleSelectSubmit">{{
          $t('withdraw_list.completion')
        }}</el-button>
      </span>
    </ElDialog>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
